/**
 * features/admin/UserManagement.tsx
 * ------------------------------------------------------------------
 * Admin assigns roles (incl. the new Editor & Head of Year) and,
 * for HOY, the year groups they cover (hoyForms). Role changes go
 * through the setUserRole Cloud Function so they're audited and
 * server-validated. Activation toggling + teacher linkage too.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import { doc, collection, onSnapshot } from 'firebase/firestore';
// Batch A: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { httpsCallable } from 'firebase/functions';
import {
  ShieldCheck,
  Search,
  Check,
  UserCog,
  UserPlus,
  Loader2,
  RefreshCw,
  Hash,
} from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES } from '@/lib/permissions';
import { assignableRolesFor, canManageUserWithRoles } from '@/lib/roleAuthority';
import { allDepartments as allDepts, deptKey } from '@/lib/departments';
import { HR_COLLECTIONS, canonicalStaffNumbers, type HrStaff } from '@/lib/hr';
import { recordHrAudit } from '@/lib/hrAudit';
import { Card, PageHeader, Spinner, Button, Badge } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import BulkUserCreation from './BulkUserCreation';
import type { Role, AppUser } from '@/types';

const FORMS = ['1', '2', '3', '4', '5', 'AS', 'A2'];

type UMTab = 'manage' | 'bulk';

export default function UserManagement() {
  const { users, teachers, subjects, loading } = useData();
  const deptSuggestions = useMemo(
    () => allDepts(subjects, teachers),
    [subjects, teachers],
  );
  const { userData, userRoles, permissions } = useAuth();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [roles, setRoles] = useState<Role[]>(['subjectTeacher']);
  const [primary, setPrimary] = useState<Role>('subjectTeacher');
  const [hoyForms, setHoyForms] = useState<string[]>([]);
  const [hodDepartments, setHodDepartments] = useState<string[]>([]);
  const [editorForms, setEditorForms] = useState<string[]>([]);
  const [linkedTeacherId, setLinkedTeacherId] = useState('');
  const [linkedStaffId, setLinkedStaffId] = useState('');
  const [hrStaff, setHrStaff] = useState<HrStaff[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<{ tempPassword: string } | null>(null);
  const [resetErr, setResetErr] = useState<string | null>(null);
  // change-login-email
  const [emailValue, setEmailValue] = useState('');
  const [emailResetPw, setEmailResetPw] = useState(true);
  const [emailSendInvite, setEmailSendInvite] = useState(true);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<{ email: string; tempPassword: string | null; emailed: boolean } | null>(null);
  // Single-user creation
  const [creating, setCreating] = useState(false);
  const [cEmail, setCEmail] = useState('');
  const [cName, setCName] = useState('');
  const [cRole, setCRole] = useState<Role>('subjectTeacher');
  const [cLinked, setCLinked] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createdResult, setCreatedResult] = useState<{ email: string; tempPassword: string; emailed: boolean } | null>(null);
  const [cEmailCreds, setCEmailCreds] = useState(true);
  // Claims sync button
  const [claimsBusy, setClaimsBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [umTab, setUmTab] = useState<UMTab>('manage');

  /* FULL identity management, or DELEGATED?
   *
   * Full is the proprietor and the system admin: delete an account, reset a
   * password, change an email, rewrite claims. Delegated is an academic
   * admin setting up teachers — they may create and re-role accounts, and
   * only within the roles lib/roleAuthority says they may assign.
   *
   * The Cloud Functions enforce all of this again and would refuse anyway;
   * hiding what cannot work is so nobody is offered a button that fails. */
  const full = permissions.canManageUsers;
  const myAssignable = useMemo(() => assignableRolesFor(userRoles), [userRoles]);
  const canTouch = (u: { roles?: Role[]; role?: Role }) =>
    full || canManageUserWithRoles(userRoles, u.roles?.length ? u.roles : (u.role ? [u.role] : []));

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return [...users]
      /* A delegated admin sees the accounts they could have created, and no
         others. Not a courtesy: this screen lists every adult in the school
         with their email and their access. */
      .filter((u) => canTouch(u))
      .filter(
        (u) =>
          u.email?.toLowerCase().includes(q) ||
          u.displayName?.toLowerCase().includes(q),
      )
      .sort(
        (a, b) =>
          (ROLES[a.role]?.priority ?? 9) - (ROLES[b.role]?.priority ?? 9) ||
          (a.displayName ?? '').localeCompare(b.displayName ?? ''),
      );
  }, [users, search, full, userRoles]); // eslint-disable-line react-hooks/exhaustive-deps

  // HR staff records, for linking a login to a staff member (admin is
  // in the HR read set, so this subscription is permitted here).
  useEffect(() => {
    const unsub = onSnapshot(collection(db, HR_COLLECTIONS.staff), (snap) => {
      setHrStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HrStaff));
    }, () => { /* ignore: link picker just stays empty */ });
    return unsub;
  }, []);

  function openEdit(u: AppUser) {
    setEditing(u);
    const rs = u.roles && u.roles.length ? u.roles : [u.role];
    setRoles(rs);
    setPrimary(rs.includes(u.role) ? u.role : rs[0]);
    setHoyForms(u.hoyForms ?? []);
    setHodDepartments(u.hodDepartments ?? []);
    setEditorForms(u.editorForms ?? []);
    setLinkedTeacherId(u.linkedTeacherId ?? '');
    setLinkedStaffId(u.linkedStaffId ?? '');
    setIsActive(u.isActive !== false);
    setMsg(null);
    setResetResult(null);
    setResetErr(null);
    setEmailValue(u.email ?? '');
    setEmailResetPw(true);
    setEmailSendInvite(true);
    setEmailErr(null);
    setEmailResult(null);
  }

  async function doDeleteUser() {
    if (!editing) return;
    if (
      !window.confirm(
        `Permanently delete ${editing.displayName ?? editing.email}?\n\nThis removes their sign-in account AND profile and cannot be undone.`,
      )
    )
      return;
    setDeleteBusy(true);
    setDeleteErr(null);
    try {
      const fn = httpsCallable(functions, 'deleteUser');
      await fn({ uid: editing.uid });
      setEditing(null);
    } catch (e) {
      setDeleteErr(
        (e as Error).message ||
          'Could not delete the user — the deleteUser function must be deployed.',
      );
    } finally {
      setDeleteBusy(false);
    }
  }

  async function doResetPassword() {
    if (!editing) return;
    setResetBusy(true);
    setResetResult(null);
    setResetErr(null);
    try {
      const fn = httpsCallable(functions, 'adminResetPassword');
      const r = await fn({ uid: editing.uid });
      const data = r.data as { tempPassword: string };
      setResetResult({ tempPassword: data.tempPassword });
    } catch (e) {
      setResetErr(
        (e as Error).message ||
        'Could not reset the password — the adminResetPassword function must be deployed.',
      );
    } finally {
      setResetBusy(false);
    }
  }

  async function doUpdateEmail() {
    if (!editing) return;
    const next = emailValue.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setEmailErr('Please enter a valid email address.');
      return;
    }
    if (next === (editing.email ?? '').toLowerCase() && !emailResetPw && !emailSendInvite) {
      setEmailErr('That is already the current email.');
      return;
    }
    setEmailBusy(true);
    setEmailErr(null);
    setEmailResult(null);
    try {
      const fn = httpsCallable(functions, 'adminUpdateUserEmail');
      const r = await fn({ uid: editing.uid, newEmail: next, alsoResetPassword: emailResetPw, sendInvite: emailSendInvite });
      const data = r.data as { email: string; tempPassword: string | null; emailed: boolean };
      setEmailResult({ email: data.email, tempPassword: data.tempPassword ?? null, emailed: !!data.emailed });
    } catch (e) {
      setEmailErr(
        (e as Error).message ||
        'Could not update the email — the adminUpdateUserEmail function must be deployed.',
      );
    } finally {
      setEmailBusy(false);
    }
  }

  function openCreate() {
    setCEmail('');
    setCName('');
    setCRole('subjectTeacher');
    setCLinked('');
    setCEmailCreds(true);
    setCreateErr(null);
    setCreatedResult(null);
    setCreating(true);
  }

  async function doCreateUser() {
    if (!cEmail.trim() || !cName.trim()) {
      setCreateErr('Email and full name are required.');
      return;
    }
    setCreateBusy(true);
    setCreateErr(null);
    setCreatedResult(null);
    try {
      const tempPassword =
        'LKC' + Math.random().toString(36).slice(2, 8).toUpperCase() + '!';
      const fn = httpsCallable(functions, 'createBulkUsers');
      const res = await fn({
        email: cEmail.trim(),
        displayName: cName.trim(),
        tempPassword,
        role: cRole,
        linkedTeacherId: cLinked || undefined,
        // When the toggle is on, createBulkUsers emails the temp password to
        // the new user via the same SMTP path as report/invoice mail. The
        // on-screen password below stays as a hand-over fallback.
        notifyEmail: cEmailCreds ? cEmail.trim() : '',
        teacherName: cName.trim(),
      });
      // createBulkUsers resolves (no throw) with { ok:false, error } when the
      // account already exists — surface that instead of a false success.
      const data = res?.data as { ok?: boolean; error?: string } | undefined;
      if (data && data.ok === false) {
        setCreateErr(data.error || 'The account could not be created.');
        return;
      }
      setCreatedResult({ email: cEmail.trim(), tempPassword, emailed: cEmailCreds });
    } catch (e) {
      setCreateErr(
        (e as Error).message ||
        'Could not create the user — the createBulkUsers function must be deployed.',
      );
    } finally {
      setCreateBusy(false);
    }
  }

  /** Toggle a role in the multi-select, keeping a valid primary. */
  function toggleRole(r: Role) {
    setRoles((prev) => {
      const next = prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r];
      if (next.length === 0) return prev; // never allow zero roles
      setPrimary((p) => (next.includes(p) ? p : next[0]));
      return next;
    });
  }

  /**
   * Claims sync button:
   * Re-applies auth custom claims for ALL users from their current
   * Firestore role. Used once for the custom-claims migration backfill,
   * and available afterwards if a claim ever drifts out of sync.
   */
  const missingStartDates = hrStaff.filter((st) => !st.startDate).length;
  async function generateStaffNumbers() {
    if (genBusy || missingStartDates > 0) return;
    if (!window.confirm(
      `This will (re)assign employee numbers to all ${hrStaff.length} staff by start date — oldest first — ` +
      `as lkc-t-NNN (teaching) and lkc-nt-NNN (non-teaching), overwriting any existing numbers. Continue?`,
    )) return;
    setGenBusy(true);
    try {
      const assignments = canonicalStaffNumbers(hrStaff);
      for (const a of assignments) {
        await setDoc(
          doc(db, HR_COLLECTIONS.staff, a.id),
          { employeeNo: a.employeeNo, updatedAt: new Date().toISOString() },
          { merge: true },
        );
      }
      await recordHrAudit({
        action: 'staff.numbers.generate',
        actor: { id: userData?.uid ?? 'unknown', email: userData?.email ?? null },
        after: { count: assignments.length },
      });
      window.alert(`Done — assigned ${assignments.length} staff numbers.`);
    } catch (e) {
      window.alert(`Could not generate staff numbers: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setGenBusy(false);
    }
  }

  async function runBackfill() {
    if (claimsBusy) return;
    if (!window.confirm(
      'Re-sync auth claims for ALL users from their current Firestore role?\n\n' +
      'Safe to run anytime — only updates the auth-side claim, not the Firestore role.\n' +
      'Users may need to sign out and back in to see updated permissions.'
    )) return;
    setClaimsBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Syncing auth claims for all users…' });
    try {
      const fn = httpsCallable(functions, 'backfillUserClaims');
      const r = await fn();
      const data = r.data as { processed: number; skipped: number; errors: Array<{ uid: string; email?: string; error: string }> };
      const summary = `Processed ${data.processed}, skipped ${data.skipped}` +
        (data.errors.length > 0 ? `, ${data.errors.length} error(s)` : '');
      if (data.errors.length > 0) {
        console.warn('[backfillUserClaims] errors:', data.errors);
        setSaveModal({
          show: true,
          status: 'error',
          message: summary + ' — see console for details.',
        });
      } else {
        setSaveModal({ show: true, status: 'success', message: summary + '.' });
      }
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setClaimsBusy(false);
    }
  }

  async function save() {
    if (!editing) return;
    if (editing.uid === userData?.uid && !roles.some((r) => r === 'admin' || r === 'systemAdmin')) {
      setMsg({
        ok: false,
        text: "You can't remove your own administrator role.",
      });
      return;
    }
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: `Updating ${editing.displayName}…` });
    try {
      // Role + hoyForms + active via the audited server function
      const fn = httpsCallable(functions, 'setUserRole');
      await fn({
        uid: editing.uid,
        role: primary,
        roles,
        hoyForms: roles.includes('hoy') ? hoyForms : [],
        editorForms: roles.includes('editor') ? editorForms : [],
        hodDepartments: roles.includes('hod') ? hodDepartments : [],
        isActive,
      });
      // Teacher linkage is a plain profile field (client write is fine)
      await setDoc(
        doc(db, 'users', editing.uid),
        {
          linkedTeacherId: linkedTeacherId || null,
          linkedStaffId: linkedStaffId || null,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
      setSaveModal({ show: true, status: 'success', message: `${editing.displayName} updated.` });
      setEditing(null);
    } catch (e) {
      setSaveModal({
        show: true,
        status: 'error',
        message:
          (e as Error).message +
          ' — the setUserRole function must be deployed.',
      });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label="Loading users…" />;

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle={`${users.length} accounts · manage roles, create accounts in bulk`}
        actions={
          <div className="flex items-center gap-2">
            {/* requireAdmin on the server — offered only where it can work. */}
            {full && (
            <Button
              variant="secondary"
              onClick={runBackfill}
              disabled={claimsBusy}
              className="flex items-center gap-2"
            >
              {claimsBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sync auth claims
            </Button>
            )}
            <Button
              variant="secondary"
              onClick={generateStaffNumbers}
              disabled={genBusy || missingStartDates > 0}
              title={missingStartDates > 0 ? `${missingStartDates} staff missing a start date` : 'Assign lkc-t / lkc-nt numbers by seniority'}
              className="flex items-center gap-2"
            >
              {genBusy ? <Loader2 size={14} className="animate-spin" /> : <Hash size={14} />}
              Generate staff numbers
            </Button>
          </div>
        }
      />

      {/* Tab toggle */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 mb-6">
        <button
          onClick={() => setUmTab('manage')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            umTab === 'manage' ? 'seg-on' : 'seg-off'
          }`}
        >
          <ShieldCheck size={15} /> Manage Users
        </button>
        <button
          onClick={() => setUmTab('bulk')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            umTab === 'bulk' ? 'seg-on' : 'seg-off'
          }`}
        >
          <UserPlus size={15} /> Bulk Create
        </button>
      </div>

      {umTab === 'bulk' && <BulkUserCreation />}

      {umTab === 'manage' && (
      <>

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="relative max-w-xs flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
            />
          </div>
          <Button icon={<UserPlus size={15} />} onClick={openCreate}>
            Add user
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Email</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium text-right">Manage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((u) => {
              const rm = ROLES[u.role];
              return (
                <tr
                  key={u.uid}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/40"
                >
                  <td className="px-4 py-2.5 font-medium">
                    {u.displayName}
                    {u.role === 'hoy' && u.hoyForms?.length ? (
                      <span className="text-xs text-gray-400 ml-2">
                        Forms {u.hoyForms.join(', ')}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={rm?.color ?? ''}>
                      {rm?.label ?? u.role}
                    </Badge>
                    {u.roles && u.roles.length > 1 ? (
                      <span
                        className="text-xs text-gray-400 ml-1.5"
                        title={u.roles.map((r) => ROLES[r]?.label ?? r).join(', ')}
                      >
                        +{u.roles.length - 1}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.isActive === false ? (
                      <span className="text-red-500 text-xs font-semibold">
                        Disabled
                      </span>
                    ) : (
                      <span className="text-emerald-600 text-xs font-semibold">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      variant="ghost"
                      icon={<UserCog size={14} />}
                      onClick={() => openEdit(u)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <FormModal
        open={!!editing}
        onClose={() => setEditing(null)}
        icon={ShieldCheck}
        title={editing?.displayName ?? 'Edit user'}
        subtitle={editing?.email ?? undefined}
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              icon={busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              disabled={busy}
              onClick={save}
            >
              Save changes
            </Button>
          </>
        }
      >
        {editing && (
          <>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Roles</label>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  Select one or more — this user's permissions are the union of all chosen roles.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(ROLES) as Role[])
                    /* Only the roles this person may assign. An academic
                       admin never sees an HR or finance role here, so the
                       boundary is visible rather than discovered by having a
                       save refused. */
                    .filter((r) => full || myAssignable.includes(r))
                    /* Pupil logins are issued by the student-accounts flow,
                       never by hand here (and the server refuses it). */
                    .filter((r) => r !== 'student')
                    .sort((a, b) => (ROLES[a].priority ?? 99) - (ROLES[b].priority ?? 99))
                    .map((r) => {
                      const on = roles.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() => toggleRole(r)}
                          title={ROLES[r].description}
                          className={`px-2.5 py-1 rounded-full text-xs border ${
                            on
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {ROLES[r].label}
                        </button>
                      );
                    })}
                </div>

                {roles.length > 1 && (
                  <div className="mt-3">
                    <label className="text-sm font-medium">Primary role</label>
                    <select
                      value={primary}
                      onChange={(e) => setPrimary(e.target.value as Role)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                    >
                      {roles
                        .slice()
                        .sort((a, b) => (ROLES[a].priority ?? 99) - (ROLES[b].priority ?? 99))
                        .map((r) => (
                          <option key={r} value={r}>
                            {ROLES[r].label}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Used for sorting, the role badge, and legacy single‑role checks.
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {ROLES[primary].description}
                </p>
              </div>

              {roles.includes('hoy') && (
                <div>
                  <label className="text-sm font-medium">
                    Year groups this HOY covers
                  </label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {FORMS.map((f) => {
                      const on = hoyForms.includes(f);
                      return (
                        <button
                          key={f}
                          type="button"
                          onClick={() =>
                            setHoyForms((h) =>
                              h.includes(f)
                                ? h.filter((x) => x !== f)
                                : [...h, f],
                            )
                          }
                          className={`px-2.5 py-1 rounded-full text-xs border ${
                            on
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          Form {f}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave none selected to cover all years.
                  </p>
                </div>
              )}

              {roles.includes('hod') && (
                <div>
                  <label className="text-sm font-medium">
                    Departments this HOD covers
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      placeholder="Type department names separated by commas (e.g. Mathematics, Sciences)"
                      value={hodDepartments.join(', ')}
                      onChange={(e) =>
                        setHodDepartments(
                          e.target.value
                            .split(',')
                            .map((d) => d.trim())
                            .filter(Boolean),
                        )
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                    />
                  </div>
                  {deptSuggestions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {deptSuggestions.map((d) => {
                        const on = hodDepartments.some((x) => deptKey(x) === deptKey(d));
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() =>
                              setHodDepartments(
                                on
                                  ? hodDepartments.filter((x) => deptKey(x) !== deptKey(d))
                                  : [...hodDepartments, d],
                              )
                            }
                            className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                              on
                                ? 'bg-[#1b3a6b] text-white border-[#1b3a6b]'
                                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[#1b3a6b]'
                            }`}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Click a department to add it. Matching against teacher
                    records is case-insensitive.
                  </p>
                </div>
              )}

              {(roles.includes('classTeacher') ||
                roles.includes('subjectTeacher') ||
                roles.includes('editor') ||
                roles.includes('hoy') ||
                roles.includes('hod')) && (
                <div>
                  <label className="text-sm font-medium">
                    Link to teacher record
                  </label>
                  <select
                    value={linkedTeacherId}
                    onChange={(e) =>
                      setLinkedTeacherId(e.target.value)
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <option value="">— none —</option>
                    {teachers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.displayName}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Links this login to a teacher for class/subject
                    scoping.
                  </p>
                </div>
              )}

              {roles.includes('staff') && (
                <div>
                  <label className="text-sm font-medium">
                    Link to staff record
                  </label>
                  <select
                    value={linkedStaffId}
                    onChange={(e) => setLinkedStaffId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <option value="">— none —</option>
                    {[...hrStaff]
                      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`))
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.employeeNo} — {s.firstName} {s.lastName}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Links this login to a staff member so they can see their own
                    payslips, leave, and tax certificate under “My Pay &amp; HR”.
                  </p>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Account active (uncheck to disable sign-in access)
              </label>
            </div>

            {/* Changing the address somebody signs in with, and resetting a
                password, are now open to a delegated account admin — but only
                for the accounts they could have created, which is the only
                set this screen shows them. The server checks the target
                again and audits both. */}
            {(full || canTouch(editing)) && (
            <div className="mt-5 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Login email</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Change the address this person signs in with. Updates their account and links, and (optionally) emails them the new login.</p>
              <input
                type="email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="name@gmail.com"
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
              />
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <input type="checkbox" checked={emailResetPw} onChange={(e) => setEmailResetPw(e.target.checked)} />
                  Also set a new temporary password
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <input type="checkbox" checked={emailSendInvite} onChange={(e) => setEmailSendInvite(e.target.checked)} />
                  Email the new login to this address
                </label>
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="ghost" disabled={emailBusy} onClick={doUpdateEmail}>
                  {emailBusy ? <Loader2 size={15} className="animate-spin" /> : 'Update login email'}
                </Button>
              </div>
              {emailErr && <p className="text-xs text-red-600 mt-1">{emailErr}</p>}
              {emailResult && (
                <div className="mt-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    Login email set to <strong>{emailResult.email}</strong>{emailResult.emailed ? ' and emailed to them.' : '.'}
                  </p>
                  {emailResult.tempPassword && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-emerald-700 dark:text-emerald-300">Temp password:</span>
                      <code className="text-sm font-bold text-[#1b3a6b] dark:text-emerald-200 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-700">{emailResult.tempPassword}</code>
                      <button type="button" onClick={() => navigator.clipboard?.writeText(emailResult.tempPassword || '')} className="text-xs font-medium text-[#2d5295] hover:underline">Copy</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            )}

            {(full || canTouch(editing)) && (
            <div className="mt-5 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Password</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Set a new temporary password to hand over — no email needed. They can change it in Settings after signing in.</p>
                </div>
                <Button variant="ghost" disabled={resetBusy} onClick={doResetPassword}>
                  {resetBusy ? <Loader2 size={15} className="animate-spin" /> : 'Reset password'}
                </Button>
              </div>
              {resetErr && <p className="text-xs text-red-600 mt-2">{resetErr}</p>}
              {resetResult && (
                <div className="mt-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">New temporary password — give this to {editing.email}:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-base font-bold text-[#1b3a6b] dark:text-emerald-200 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-700">{resetResult.tempPassword}</code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(resetResult.tempPassword)}
                      className="text-xs font-medium text-[#2d5295] hover:underline"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>

            )}

            {/* §4 Danger zone — fully delete a user (Auth + profile). Hidden for
                self, for admin accounts, and for a delegated admin: deleting
                accounts is full identity management. The server enforces all
                three. */}
            {full &&
              userData?.uid !== editing.uid &&
              !(editing.roles ?? (editing.role ? [editing.role] : [])).some(
                (r) => r === 'admin' || r === 'systemAdmin',
              ) && (
                <div className="mt-5 rounded-xl border border-red-200 dark:border-red-800 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">Delete user</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Permanently removes the sign-in account and profile. This cannot be undone.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={deleteBusy}
                      onClick={doDeleteUser}
                      className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 dark:bg-red-900/20 dark:text-red-300"
                    >
                      {deleteBusy ? 'Deleting…' : 'Delete user'}
                    </button>
                  </div>
                  {deleteErr && <p className="text-xs text-red-600 mt-2">{deleteErr}</p>}
                </div>
              )}
          </>
        )}
      </FormModal>

      <FormModal
        open={creating}
        onClose={() => setCreating(false)}
        icon={UserPlus}
        title="Add user"
        maxWidth="max-w-md"
      >
              <p className="text-xs text-gray-500 mb-4">Creates a sign-in account with a temporary password. By default the login details are emailed to the user; the password is also shown here as a fallback. Extra roles can be added by editing the user afterwards.</p>

              {!createdResult ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Email</label>
                    <input
                      type="email"
                      value={cEmail}
                      onChange={(e) => setCEmail(e.target.value)}
                      placeholder="teacher@lkc.ac.bw"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Full name</label>
                    <input
                      value={cName}
                      onChange={(e) => setCName(e.target.value)}
                      placeholder="Jane Moyo"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Role</label>
                    <select
                      value={cRole}
                      onChange={(e) => setCRole(e.target.value as Role)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                    >
                      {(Object.keys(ROLES) as Role[])
                        .filter((r) => full || myAssignable.includes(r))
                        .filter((r) => r !== 'student')
                        .sort((a, b) => (ROLES[a].priority ?? 99) - (ROLES[b].priority ?? 99))
                        .map((r) => <option key={r} value={r}>{ROLES[r].label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Linked teacher (optional)</label>
                    <select
                      value={cLinked}
                      onChange={(e) => setCLinked(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <option value="">— none —</option>
                      {teachers
                        .slice()
                        .sort((a, b) => a.fullName.localeCompare(b.fullName))
                        .map((t) => <option key={t.id} value={t.id}>{t.fullName}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300 pt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cEmailCreds}
                      onChange={(e) => setCEmailCreds(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    Email the login details to this user
                  </label>
                  {createErr && <p className="text-xs text-red-600">{createErr}</p>}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" disabled={createBusy} onClick={() => setCreating(false)}>Cancel</Button>
                    <Button
                      icon={createBusy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
                      disabled={createBusy}
                      onClick={doCreateUser}
                    >
                      Create user
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">Account created for {createdResult.email}.{createdResult.emailed ? ' The login details have been emailed to them.' : ''} Temporary password{createdResult.emailed ? ' (also shown here as a fallback)' : ' — give this to them'}:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-base font-bold text-[#1b3a6b] dark:text-emerald-200 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-700">{createdResult.tempPassword}</code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(createdResult.tempPassword)}
                        className="text-xs font-medium text-[#2d5295] hover:underline"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">They can change it in Settings after signing in.</p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={openCreate}>Add another</Button>
                    <Button onClick={() => setCreating(false)}>Done</Button>
                  </div>
                </div>
              )}
      </FormModal>

      </>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
